import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';

/** One segment of an {@link SbSegmentedComponent}. */
export interface SbSegment {
  /** Value emitted when this segment is chosen. */
  readonly value: string;
  /** Visible label. Also the accessible name. */
  readonly label: string;
  /**
   * Not choosable. The segment renders disabled AND carries `aria-disabled`
   * AND refuses the click — appearance, accessible state and behaviour agree
   * (HANDOFF §11b). The reason is linked with `aria-describedby`.
   */
  readonly disabled?: boolean;
}

let sbSegmentedUid = 0;

/**
 * sb-segmented — the single-choice segmented row Smart Book uses for a day's
 * Office · Home · Elsewhere · Out decision.
 *
 * WHY NOT `paper-segmented-control`
 *   The Paper atom takes `{value, label, icon, count}` and has no per-option
 *   disabled state. Smart Book needs one: the platform rejects a manual work
 *   status while an in-office booking exists, so the three non-Office segments
 *   must LOCK — visibly, accessibly and behaviourally — until the booking is
 *   released. Rendering them enabled and dropping the click on the floor is
 *   exactly the "a control whose appearance and behaviour disagree" defect
 *   §11b calls out. Fold `disabled` + `disabledReason` into the Paper atom and
 *   this component should be deleted in favour of it.
 *
 * A11Y
 *   `role="radiogroup"` with roving tabindex: only the checked segment (or the
 *   first enabled one when nothing is checked) is tabbable; arrows move the
 *   choice, Home/End jump to the first/last enabled segment. Disabled segments
 *   are skipped by the arrows and point at the shared reason via
 *   `aria-describedby`.
 */
@Component({
  selector: 'sb-segmented',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sb-segmented.component.html',
  styleUrl: './sb-segmented.component.scss',
})
export class SbSegmentedComponent {
  readonly options = input.required<readonly SbSegment[]>();
  /** The chosen value, or null when the viewer has chosen nothing. */
  readonly value = input<string | null>(null);
  readonly ariaLabel = input<string | undefined>(undefined);
  /**
   * Why the disabled segments are disabled. Rendered once, visually hidden,
   * and linked from every disabled segment. Omit when nothing is disabled.
   */
  readonly disabledReason = input<string | undefined>(undefined);
  /** Compact row — used inside the dense day rows. */
  readonly dense = input(false);
  /**
   * Fill the space the host gives it, with the four segments sharing the
   * width equally. This is the day-row shape in the design: one full-width
   * four-way control per day, not a small pill floated to the right.
   */
  readonly stretch = input(false);

  readonly valueChange = output<string>();

  private readonly root = viewChild<ElementRef<HTMLElement>>('root');

  protected readonly reasonId = `sb-seg-reason-${++sbSegmentedUid}`;

  /**
   * The one tabbable segment. The chosen value wins; with nothing chosen (or
   * a chosen value that is disabled) the first enabled segment takes the tab
   * stop so the control is always reachable by keyboard.
   */
  protected readonly tabbableValue = computed(() => {
    const options = this.options();
    const current = options.find(o => o.value === this.value() && !o.disabled);
    return (current ?? options.find(o => !o.disabled))?.value ?? null;
  });

  protected select(option: SbSegment): void {
    if (option.disabled || option.value === this.value()) return;
    this.valueChange.emit(option.value);
  }

  protected onKeydown(event: KeyboardEvent, index: number): void {
    const options = this.options();
    const enabled = options.filter(o => !o.disabled);
    if (enabled.length === 0) return;

    let target: SbSegment | undefined;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = this.step(index, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = this.step(index, -1);
        break;
      case 'Home':
        target = enabled[0];
        break;
      case 'End':
        target = enabled[enabled.length - 1];
        break;
      default:
        return;
    }
    if (!target) return;
    event.preventDefault();
    this.select(target);
    this.focusValue(target.value);
  }

  /** Next enabled segment in `direction`, wrapping. Undefined when none. */
  private step(from: number, direction: 1 | -1): SbSegment | undefined {
    const options = this.options();
    const count = options.length;
    for (let hop = 1; hop <= count; hop++) {
      const candidate = options[(((from + direction * hop) % count) + count) % count];
      if (candidate && !candidate.disabled) return candidate;
    }
    return undefined;
  }

  private focusValue(value: string): void {
    const host = this.root()?.nativeElement;
    host?.querySelector<HTMLButtonElement>(`[data-value="${value}"]`)?.focus();
  }
}
